// Extra depth for the original Effy Order feature pages in seoPages.js, added by path:
// a readable `guide` text, one more section, more FAQs, more related links and (where the
// old title was too long for Google to show) a shorter metaTitle.
// seoPages.js merges these into each page. Pages stay English-only.
// Only describe things the app really does.

export const PAGE_EXTRAS = {
  '/products/effy-order/inventory-management-software': {
    metaTitle: 'Inventory Management Software for India | Effy Order',
    guide: [
      {
        title: 'What good inventory management looks like in a small shop',
        paragraphs: [
          'Inventory is the money you have tied up in goods. If you hold too little, customers walk out without what they came for. If you hold too much, cash sits on the shelf and slow items get old, damaged or out of date. Inventory management is the habit of knowing, at any moment, what you have, what it cost and what is selling.',
          'For a small business this does not need a warehouse system. It needs one place where every item is created once, with its SKU, HSN code, unit, purchase price and selling price, and where the count moves by itself as you trade.',
        ],
      },
      {
        title: 'How stock moves in Effy Order',
        paragraphs: [
          'Stock goes down when an order is created and up when a purchase order is received. A credit note for a returned item puts it back on the shelf. Because these movements are part of the normal billing work, nobody has to remember a separate stock entry at the end of the day.',
          'Items can also hold batches, each with its own quantity, prices and expiry date, for goods bought at different times or prices. If you sell something that has nothing to count, such as a service, you can switch stock updates off for that item so it never shows a false shortage.',
        ],
      },
      {
        title: 'From counting to deciding',
        paragraphs: [
          'Once the numbers are right you can use them. Low stock alerts tell you what to order. Stale stock insights point at goods that are not moving, so you can put them on offer. The Inventory Report gives stock levels, valuation, daily stock movement and expiry lists as downloadable files.',
          'Start by uploading your catalogue from Excel, set the opening quantities, and from the next sale onward the app keeps the count. Check a few fast-moving items against the shelf in the first week, and you will know quickly that the numbers can be trusted.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Item details',
        title: 'One record for every product',
        description: 'Keep everything about a product in one place so billing, purchasing and reports all read the same facts.',
        points: [
          'SKU, HSN code, unit, purchase price and selling price on every item.',
          'Categories to group items and see sales by category.',
          'Batches with their own quantity and expiry date.',
          'Stock updates can be switched off for service items.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'How is inventory management different from stock management?', answer: 'The two terms are used for the same everyday job: knowing how much of each product you hold. Inventory management also covers the item details, prices and purchasing around the count. Effy Order does both.' },
      { question: 'Can I keep different batches of the same item?', answer: 'Yes. An item can have batches, each with its own quantity, prices and expiry date, which helps when you buy the same product at different times.' },
      { question: 'What reports does the inventory module give?', answer: 'The Inventory Report offers a full report, low stock, stock availability, stock valuation, daily stock movement, expiry and item-wise sales and profit.' },
    ],
    extraRelated: ['/products/effy-order/stock-management-software', '/inventory-management-for-small-business', '/stock-management-excel-vs-software'],
  },
  '/products/effy-order/gst-billing-software': {
    metaTitle: 'GST Billing Software & Invoice Generator | Effy Order',
    guide: [
      {
        title: 'What GST billing software should do for you',
        paragraphs: [
          'GST billing software has one job at its heart: put the right tax on every bill without making the person at the counter think about it. That means holding a tax rate for each product, choosing between CGST plus SGST and IGST, and showing the figures clearly on the invoice. Everything else, from stock to reports, is built on top of that.',
          'In Effy Order the rate is saved once against the HSN code in a tax profile. The counter staff add items, and the tax is picked up on its own. If a customer is in another state, the invoice shows IGST in place of the two local taxes.',
        ],
      },
      {
        title: 'Getting the maths right with discounts and charges',
        paragraphs: [
          'Most shops give discounts, and many add a delivery or packing charge. These change the value on which tax is calculated, which is where hand-made bills go wrong. Effy Order works out the tax after item-wise and overall discounts, and lets an additional charge carry its own tax.',
          'The invoice prints with the item lines, tax split and totals, plus warranty details when you have entered them. You can print it, or send the details to the customer by WhatsApp or email.',
        ],
      },
      {
        title: 'After the bill: returns, dues and reports',
        paragraphs: [
          'A bill is not the end of the story. Customers return goods, pay part now and part later or pay an advance. Credit notes and debit notes handle returns and corrections, payment-in receipts handle later payments and advances can be adjusted on the next order.',
          'When the month closes, the GST report and the sales report summarise what you billed. They are working summaries for your accountant. For a longer explanation of how to pick a product, read our guide on choosing GST billing software in India.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Setup',
        title: 'Get your tax profile ready once',
        description: 'A little setup at the start means no tax decisions at the counter later.',
        points: [
          'Add each HSN code with its tax rate.',
          'Attach the HSN code to every item in your catalogue.',
          'Add the customer state so the CGST, SGST or IGST split is chosen correctly.',
          'Bulk upload items from Excel to avoid typing them one by one.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'What is a GST invoice generator?', answer: 'A GST invoice generator creates a tax invoice with the item lines, tax rates and totals filled in for you. Effy Order does this from your HSN-wise tax profile and also updates stock and reports.' },
      { question: 'Can I bill without internet at the counter?', answer: 'Effy Order runs in a web browser and needs an internet connection to save bills. Plan for a working connection at the counter.' },
      { question: 'Can I use it on a phone?', answer: 'Yes. It works in the browser on a phone, tablet or computer, and express mode is designed for touch screens.' },
    ],
    extraRelated: ['/products/effy-order/gst-report-software', '/products/effy-order/invoice-billing-software', '/gst-billing-software-india', '/gst-invoice-format-india'],
  },
  '/products/effy-order/order-management-software': {
    metaTitle: 'Order Management Software for Retail | Effy Order',
    guide: [
      {
        title: 'Why orders need their own system',
        paragraphs: [
          'An order is a promise: this customer will get these items at this price by this date. When orders live in phone calls, chat messages and scraps of paper, promises are missed. Order management software puts each promise on one record so that anybody in the business can see its state.',
          'Effy Order treats the order as the centre. Stock, invoices, delivery challans, payments and customer notifications all hang off it, so the information is entered once and used in many places.',
        ],
      },
      {
        title: 'Following an order from start to finish',
        paragraphs: [
          'A new order starts as Initiated, moves to In Progress while it is being prepared and ends as Completed when it is delivered. Alongside that runs the payment status of Pending, Partially Paid or Paid. Together they answer the two questions customers always ask: where is my order, and what do I still owe.',
          'You can filter the order list by status, payment, customer or invoice number, so finding one order among thousands takes seconds. Delivery dates and notes sit on the order, and customers can be told about it on WhatsApp and email.',
        ],
      },
      {
        title: 'Handling volume',
        paragraphs: [
          'Some businesses take a handful of orders a day, others receive large batches from a marketplace or a sales team. For the second kind, orders can be uploaded from an Excel sheet, and the app tells you how many rows were saved and how many failed, so you can correct only the failures.',
          'Customers are saved automatically the first time you bill them, which means repeat buyers can be found by phone number. Over time you build a history that shows who orders often, who pays late and what each person buys.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Control',
        title: 'Keep the team on the same page',
        description: 'Everybody sees the same order, so there is less asking around.',
        points: [
          'Edit an order and the stock, totals and documents follow.',
          'Convert a quotation into an order when the customer agrees.',
          'Record who made the sale where employee tracking is enabled.',
          'Open any order to see its invoice, challan and payments.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'How do I find one particular order quickly?', answer: 'Use the filters on the order list. You can search by customer, invoice number, order status or payment status.' },
      { question: 'Does editing an order change the stock?', answer: 'Yes. Stock follows the order, so when you change items or quantities the stock is adjusted to match.' },
      { question: 'Can I turn a quotation into an order?', answer: 'Yes. An approved quotation converts into an order without retyping the items.' },
    ],
    extraRelated: ['/products/effy-order/sales-management-software', '/products/effy-order/sales-report-software'],
  },
  '/products/effy-order/delivery-challan-software': {
    metaTitle: 'Delivery Challan Software and Print | Effy Order',
    guide: [
      {
        title: 'Why delivery staff need a clean challan',
        paragraphs: [
          'The person who carries goods to a customer is not the person who made the bill. They need a short document that says what is in the parcel and where it is going. A challan does that, and the signed copy that comes back is proof that the delivery was made.',
          'When challans are typed separately, they drift away from the invoice. The quantity on the challan says ten, the invoice says twelve, and the argument starts at the customer’s door.',
        ],
      },
      {
        title: 'One order, matching documents',
        paragraphs: [
          'In Effy Order the challan is generated from the order, not typed again. Items, quantities and customer details are copied across, so whatever you print is what was sold. The invoice and the estimate come from the same order, which means a correction made once is seen in every document.',
          'The order also carries a delivery date, so your team can see what is due. As the order moves from Initiated to Completed, the status shows what has gone out and what is waiting.',
        ],
      },
      {
        title: 'Printing and sharing',
        paragraphs: [
          'Print the challan on normal paper or use the thermal receipt layout if your delivery counter uses a roll printer. For the cleanest thermal print, pick the roll paper size in the printer settings and turn off the browser header and footer.',
          'Customers can also be told about the order by WhatsApp and email, so they expect the delivery. All challans can be browsed in the billing section, which helps when a customer calls to ask about a delivery last week.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Tips',
        title: 'Habits that make challans useful',
        description: 'A few simple habits turn a printed page into real control over deliveries.',
        points: [
          'Print the challan before the goods leave the shop.',
          'Keep the signed copy with the matching invoice.',
          'Set a delivery date on the order so due deliveries are visible.',
          'Mark the order Completed only after delivery.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'Can I print a challan on a thermal printer?', answer: 'Yes. Effy Order has a thermal layout for roll printers as well as a standard page layout.' },
      { question: 'Where can I see all my challans?', answer: 'All delivery challans can be browsed in the billing section of Effy Order.' },
    ],
    extraRelated: ['/delivery-challan-format', '/products/effy-order/invoice-billing-software'],
  },
  '/products/effy-order/purchase-order-software': {
    metaTitle: 'Purchase Order Software for Suppliers | Effy Order',
    guide: [
      {
        title: 'Buying is half of the business',
        paragraphs: [
          'Most billing tools look only at sales, but a shop earns or loses money on what it buys as well. Purchases decide what is on your shelf, what you owe to suppliers and how much cash goes out each month. A purchase order is the written request to a supplier, and it is the start of the record that follows the goods into your stock.',
          'Effy Order keeps purchases beside sales, so when goods arrive the stock goes up in the same system you bill from.',
        ],
      },
      {
        title: 'From purchase order to stock',
        paragraphs: [
          'You pick a supplier, add items from your inventory, and print the purchase order for the supplier. When the goods are received, the quantities are added to your stock. You can open any item and see every purchase order that touched it, and every sale as well.',
          'Because purchase prices are recorded with the items, profit can be shown in reports such as the closing report and the item-wise sales and profit report. The Supplier Details Report goes further and shows sales, profit and quantity sold out of each supplier’s stock.',
        ],
      },
      {
        title: 'Paying suppliers and handling returns',
        paragraphs: [
          'When you pay a supplier, record the payment and print a receipt, so there is a written record of what went out. If goods go back to the supplier, a debit note records the return and takes the items off your stock.',
          'This gives you a list of what you owe each supplier and a history you can check when a supplier’s statement does not match your own. Over time it also tells you which suppliers give you the best-selling stock.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Reports',
        title: 'See which supplier’s stock sells best',
        description: 'Purchases are linked to sales, so you can judge suppliers by results.',
        points: [
          'Supplier Details Report for sales, profit and quantity sold.',
          'Purchases, returns and item-wise sales for each supplier.',
          'Purchase history per item.',
          'Pending amounts to suppliers.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'Can I see how much profit a supplier’s stock earned?', answer: 'Yes. The Supplier Details Report shows all-time sales, profit and quantity sold out of each supplier’s stock, with purchases, returns and item-wise sales for each supplier.' },
      { question: 'What happens when I return goods to a supplier?', answer: 'A debit note records the return and the stock is adjusted.' },
      { question: 'Can I print purchase payment receipts?', answer: 'Yes. Each purchase payment can be printed as a receipt.' },
    ],
    extraRelated: ['/products/effy-order/stock-management-software', '/inventory-management-for-small-business'],
  },
  '/products/effy-order/quotation-software': {
    metaTitle: 'Quotation and Estimate Software | Effy Order',
    guide: [
      {
        title: 'Why a written quotation wins orders',
        paragraphs: [
          'Customers who ask for a price often compare two or three shops. The one that replies with a clear written quotation, listing each item, its price and the discount, looks more reliable than the one that gives a number over the phone. A quotation also protects you, because the price you offered is on record.',
          'Effy Order makes the quotation a normal part of selling. You create it with the same item list, taxes and discounts as an invoice, so the figures never change by accident when the sale goes through.',
        ],
      },
      {
        title: 'From quote to order without retyping',
        paragraphs: [
          'Print the quotation as an estimated bill and give it to the customer. When the customer says yes, convert the quotation into an order. Items, prices and discounts carry across, and you invoice from the order in the usual way.',
          'This saves time, but more importantly it avoids the small errors that creep in when the same list is typed twice. All quotations are kept in one list, so you can find the ones that are still waiting for an answer.',
        ],
      },
      {
        title: 'Quotation, estimate and invoice',
        paragraphs: [
          'The three documents are easy to mix up. A quotation or estimate is an offer made before the sale. An invoice is the bill after the sale. A delivery challan travels with the goods. In Effy Order all of them are made from the same record, so the offer, the bill and the delivery note always agree.',
          'If you quote often for large items, check the quotation list at the start of each week and call the customers who have not replied. A simple follow-up is the cheapest way to win more orders.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Follow-up',
        title: 'Do not lose track of open quotes',
        description: 'A quote is only useful if somebody follows it up.',
        points: [
          'See every quotation in one list.',
          'Customer history shows what they bought before.',
          'Convert to an order the day the customer agrees.',
          'Print the estimated bill again whenever it is needed.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'Can a quotation include GST?', answer: 'Yes. Quotations use the same items, discounts and tax as invoices, so the quote matches the final bill.' },
      { question: 'Is an estimate the same as a quotation?', answer: 'The two words are used for the same thing in everyday trade: a price offer before the sale. Effy Order prints it as an estimated bill.' },
    ],
    extraRelated: ['/products/effy-order/sales-management-software'],
  },
  '/products/effy-order/barcode-billing-software': {
    metaTitle: 'Barcode Billing Software for Retail Shops | Effy Order',
    guide: [
      {
        title: 'Why barcodes speed up the counter',
        paragraphs: [
          'Typing item names at a busy counter is slow and invites mistakes. A barcode scan finds exactly the right item and adds it with its price in a moment. Scanning also keeps stock honest, because the item sold is the item removed from stock.',
          'Effy Order supports a barcode scanner as well as the camera on a phone or tablet, so a small shop can start scanning without buying new hardware.',
        ],
      },
      {
        title: 'Products without a barcode',
        paragraphs: [
          'Not every product arrives with a printed barcode. For loose items, local brands and things you pack yourself, Effy Order has a barcode generator and lets you print labels for your shelves. Once labelled, those items are scanned like any other.',
          'You can add new items by scanning their barcode, and you can change the count of an item by scanning it in express quantity update, which is a quick way to do a stock check along a shelf.',
        ],
      },
      {
        title: 'Setting up the counter',
        paragraphs: [
          'A scanner that behaves like a keyboard, and sends an Enter key after each scan, works well with the billing screen. In the full billing form and in sale order editing, a scanned code adds the item or increases its quantity if it is already on the bill.',
          'Choose the POS billing look for a supermarket-style checkout, or express mode for a tablet. Voice search is also there for finding items by name. After each bill, stock and reports update by themselves.',
        ],
      },
    ],
    extraSections: [
      {
        label: 'Hardware',
        title: 'Start with what you already have',
        description: 'You do not need an expensive POS machine to begin scanning.',
        points: [
          'A USB or Bluetooth barcode scanner that works as a keyboard.',
          'The camera on a phone or tablet.',
          'A normal printer or a roll printer for labels and bills.',
          'Any modern browser on the device you already use.',
        ],
      },
    ],
    extraFaqs: [
      { question: 'What kind of barcode scanner works with Effy Order?', answer: 'A scanner that types the code like a keyboard and sends an Enter key after each scan works with the billing screen. Phone and tablet cameras can also scan.' },
      { question: 'What happens if I scan an item that is already on the bill?', answer: 'The quantity of that item goes up by one instead of adding a second line.' },
      { question: 'Can I do a stock check by scanning?', answer: 'Yes. Express quantity update lets you scan an item and change its count.' },
    ],
    extraRelated: ['/products/effy-order/stock-management-software', '/products/effy-order/billing-software-for-small-business'],
  },
};
